import { Button } from '@acpaas-ui/react-components';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import { set } from '../../actions/breadcrumbs.actions';
import { SCHOOL_FIELDS } from '../../constants/admin.schools.constants';
import { MODALS } from '../../constants/modals.constants';
import {
  ADMIN_ORGANIZATIONS_PATHS,
  ADMIN_PATH,
  ADMIN_SCHOOLS_PATHS,
  ADMIN_TAB_PATHS
} from '../../constants/paths.constants';
import { useForm } from '../../hooks/useForm.hook';
import { useModal } from '../../hooks/useModal.hook';
import DeleteWarningModal from '../../modals/DeleteWarningModal';
import { breadcrumbsSelector } from '../../selectors/breadcrumbs.selectors';
import adminSchoolsService from '../../services/api/admin.schools.service';
import AdminSchoolDetailsField from './AdminSchoolDetailsField';

const AdminSchoolDetails = ({ school, dependencies = {}, params }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const breadcrumbs = useSelector(breadcrumbsSelector);
  const agodiData = school?.spoEntityData?.agodi;
  const shadowData = school?.spoEntityData?.shadow ?? {};
  const metaData = school?.spoEntityMeta ?? {};
  const deleteModal = useModal(MODALS.DELETE_SCHOOL_MODAL);
  const { isSubmitting, data, error, reset, input, submitStart, submitOk, submitError } = useForm({
    ...agodiData,
    ...shadowData,
    isVisible: metaData.isVisible
  });
  const resource = {
    isFetching: false,
    isSubmitting,
    getValue: (field) => data?.[field],
    setValue: (value, field, options = {}) => {
      submitStart();
      adminSchoolsService.update(
        params.netId,
        params.managementId,
        params.communityId,
        params.schoolId,
        { [field]: value },
        {
          onSuccess: () => {
            submitOk();
            input(value, field);
            if (field === 'name') {
              dispatch(
                set(
                  breadcrumbs?.map((b, index) => (index === breadcrumbs?.length - 1 ? { ...b, name: value } : b)) ?? []
                )
              );
            }
            ReactTooltip.rebuild();
            options.onSuccess();
          },
          onError: (message) => {
            submitError({ [field]: message });
            options.onError(message);
          }
        }
      );
    },
    validators: {},
    onCancel: reset
  };

  const handleDelete = (onError, onSuccess) => {
    adminSchoolsService.remove(params.netId, params.managementId, params.communityId, params.schoolId, {
      onError,
      onSuccess: () => {
        onSuccess();
        navigate(
          generatePath(
            `${ADMIN_PATH}${ADMIN_TAB_PATHS.ORGANIZATIONS.path}${ADMIN_ORGANIZATIONS_PATHS.SCHOOLS.path}${ADMIN_SCHOOLS_PATHS.SCHOOLS.path}`,
            params
          )
        );
      }
    });
  };

  const handleVisibilityChange = (value) => {
    const options = {
      onSuccess: () => {
        submitOk();
        input(value, 'isVisible');
      },
      onError: (message) => {
        submitError({ isVisible: message });
      }
    };
    submitStart();
    if (!!value) {
      adminSchoolsService.setSchoolVisible(
        params.netId,
        params.managementId,
        params.communityId,
        params.schoolId,
        options
      );
    } else {
      adminSchoolsService.setSchoolInvisible(
        params.netId,
        params.managementId,
        params.communityId,
        params.schoolId,
        options
      );
    }
  };

  return (
    <div className="spo-m-admin-school-details">
      <div className="row">
        {SCHOOL_FIELDS.map((field) => (
          <AdminSchoolDetailsField
            key={`${field.field}_${params.schoolId}`}
            options={dependencies?.[field.field]}
            resource={resource}
            field={field}
            value={data?.[field.field]}
            agodiValue={agodiData?.[field.field]}
            error={error?.[field.field]}
            onVisibilityChange={handleVisibilityChange}
            isManual={metaData?.isManual}
            isSubmitting={isSubmitting}
          />
        ))}
      </div>
      {metaData?.isManual && (
        <div className="u-flex">
          <Button className="u-margin-left-auto u-margin-top-md" type="danger" outline onClick={deleteModal.onOpen}>
            Verwijderen
          </Button>
          <DeleteWarningModal
            {...deleteModal}
            warningText="Ben je zeker dat je deze school wilt verwijderen? Alle informatie die hieraan gekoppeld is zal verloren gaan."
            onConfirm={handleDelete}
          />
        </div>
      )}
    </div>
  );
};

export default AdminSchoolDetails;
